import React, { useRef, useState } from 'react';
import { holeToken } from '../../../lib/sitzung';
import { MIcon } from '../../../lib/micon';
import { API_BASE } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { mikrofonAnfragen, zeigeMikrofonFehler } from '../../../lib/mikrofon';

/** Aufnahme → /api/transkribiere → Text. Gemeinsam für Feld-Mikrofone und „Erzählen“. */
export function useFeldDiktat(onText: (text: string) => void | Promise<void>, onTippen?: () => void) {
  const [zustand, setZustand] = useState<'bereit' | 'aufnahme' | 'verarbeitet'>('bereit');
  const rec = useRef<MediaRecorder | null>(null);
  async function start() {
    let stream: MediaStream;
    // iOS Safari: getUserMedia direkt im Tipp-Kontext
    try { stream = await mikrofonAnfragen(); }
    catch (err) { zeigeMikrofonFehler(err, { stattdessenTippen: onTippen }); return; }
    const r = new MediaRecorder(stream);
    rec.current = r;
    const teile: BlobPart[] = [];
    r.ondataavailable = e => { if (e.data.size > 0) teile.push(e.data); };
    r.onstop = async () => {
      stream.getTracks().forEach(t => t.stop());
      setZustand('verarbeitet');
      try {
        const fd = new FormData();
        fd.append('audio', new Blob(teile, { type: 'audio/webm' }), 'diktat.webm');
        // FormData: nur Authorization setzen (kein JSON-Content-Type, sonst sieht multer keine Datei)
        const jwt = holeToken() ?? '';
        const res = await fetch(`${API_BASE}/api/transkribiere`, { method: 'POST', headers: { Authorization: `Bearer ${jwt}` }, body: fd });
        const d = await res.json().catch(() => ({} as any));
        if (!res.ok) throw new Error(d.error === 'kontingent' ? d.message : (d.fehler ?? 'Transkription fehlgeschlagen'));
        if (d.text?.trim()) await onText(d.text.trim());
      } catch (err: any) { toast(err?.message ?? 'Diktat fehlgeschlagen', 'error'); }
      finally { setZustand('bereit'); }
    };
    r.start();
    setZustand('aufnahme');
  }
  function stop() { rec.current?.stop(); }
  return { zustand, start, stop };
}

/** Kleiner Mikrofon-Knopf in einem Textfeld: hängt das Diktat an den Text an */
export function DiktatFeldKnopf({ onText, label = 'Erzählen' }: { onText: (text: string) => void; label?: string }) {
  const d = useFeldDiktat(onText);
  return (
    <button type="button" className={`ld-dfk${d.zustand === 'aufnahme' ? ' ld-dfk--an' : ''}`}
      onClick={() => (d.zustand === 'aufnahme' ? d.stop() : d.zustand === 'bereit' ? d.start() : undefined)}
      aria-label={d.zustand === 'aufnahme' ? 'Aufnahme beenden' : label} title={d.zustand === 'aufnahme' ? 'Aufnahme beenden' : label}
      disabled={d.zustand === 'verarbeitet'}>
      <MIcon name={d.zustand === 'aufnahme' ? 'stop' : d.zustand === 'verarbeitet' ? 'hourglass_top' : 'mic'} size={18} />
    </button>
  );
}
